import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Eye,
  Trash2,
  LogOut,
  Ban,
  ShieldOff,
  UserCheck,
  XCircle,
  Plus,
} from 'lucide-react';
import { CurrencyLabel } from '../CurrencyDisplay';

/**
 * Shared housing add/edit dialog (same fields for both modes).
 */
export const HousingFormDialog = ({ open, onOpenChange, mode, form, setForm, onSubmit }) => {
  const isAdd = mode === 'add';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-white/10">
        <DialogHeader>
          <DialogTitle>{isAdd ? 'إضافة سكن جديد' : 'تعديل السكن'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">اسم السكن</label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={isAdd ? 'مثال: سكن 1' : ''}
              required
              className="bg-secondary/50 border-white/10"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              <CurrencyLabel label="الإيجار السنوي" />
            </label>
            <Input
              type="number"
              value={form.annual_rent}
              onChange={(e) => setForm({ ...form, annual_rent: parseFloat(e.target.value) || 0 })}
              className="bg-secondary/50 border-white/10"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              <CurrencyLabel label="قيمة الأثاث" />
            </label>
            <Input
              type="number"
              value={form.furniture_cost}
              onChange={(e) =>
                setForm({ ...form, furniture_cost: parseFloat(e.target.value) || 0 })
              }
              className="bg-secondary/50 border-white/10"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium mb-2">الكهرباء</label>
              <Input
                type="number"
                value={form.electricity_bill}
                onChange={(e) =>
                  setForm({ ...form, electricity_bill: parseFloat(e.target.value) || 0 })
                }
                className="bg-secondary/50 border-white/10"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">الماء</label>
              <Input
                type="number"
                value={form.water_bill}
                onChange={(e) => setForm({ ...form, water_bill: parseFloat(e.target.value) || 0 })}
                className="bg-secondary/50 border-white/10"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">الإنترنت</label>
              <Input
                type="number"
                value={form.internet_bill}
                onChange={(e) =>
                  setForm({ ...form, internet_bill: parseFloat(e.target.value) || 0 })
                }
                className="bg-secondary/50 border-white/10"
              />
            </div>
          </div>
          <Button type="submit" className="w-full bg-primary text-primary-foreground">
            {isAdd ? (
              <>
                <Plus className="w-4 h-4 me-2" /> إضافة السكن
              </>
            ) : (
              'حفظ التغييرات'
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ViewersDialog = ({ open, onOpenChange, selectedHousing, allUsers, onRemoveViewer, onAddViewer }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-card border-white/10">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Eye className="w-5 h-5 text-primary" />
          إدارة المشاهدين
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">المشاهدون الحاليون</label>
          <div className="space-y-2">
            {selectedHousing?.viewers?.length > 0 ? (
              selectedHousing.viewers.map((viewerId) => {
                const viewer = allUsers.find((u) => u.id === viewerId);
                return (
                  viewer && (
                    <div
                      key={viewerId}
                      className="flex items-center justify-between p-2 bg-secondary/30 rounded-lg"
                    >
                      <span>{viewer.name}</span>
                      <Button
                        onClick={() => onRemoveViewer(viewerId)}
                        variant="ghost"
                        size="sm"
                        className="text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )
                );
              })
            ) : (
              <p className="text-muted-foreground text-sm">لا يوجد مشاهدون</p>
            )}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">إضافة مشاهد</label>
          <Select onValueChange={onAddViewer}>
            <SelectTrigger className="bg-secondary/50 border-white/10">
              <SelectValue placeholder="اختر مستخدم" />
            </SelectTrigger>
            <SelectContent className="bg-card border-white/10">
              {allUsers
                .filter((u) => !selectedHousing?.viewers?.includes(u.id) && u.role !== 'admin')
                .map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name} ({u.role})
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export const EvacuationDialog = ({ open, onOpenChange, form, setForm, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-card border-white/10">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <LogOut className="w-5 h-5 text-orange-400" />
          طلب إخلاء ساكن
        </DialogTitle>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">تاريخ الإخلاء</label>
          <Input
            type="date"
            value={form.evacuation_date}
            onChange={(e) => setForm({ ...form, evacuation_date: e.target.value })}
            min={new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
            required
            className="bg-secondary/50 border-white/10"
          />
          <p className="text-xs text-muted-foreground mt-1">يجب أن يكون قبل يومين على الأقل</p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">السبب (اختياري)</label>
          <Input
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            className="bg-secondary/50 border-white/10"
          />
        </div>
        <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700 text-white">
          إرسال طلب الإخلاء
        </Button>
      </form>
    </DialogContent>
  </Dialog>
);

export const BlockedBarbersDialog = ({
  open,
  onOpenChange,
  blockedBarbers,
  allUsers,
  onUnblock,
  onBlock,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-card border-white/10">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Ban className="w-5 h-5 text-red-400" />
          إدارة الحلاقين المحجوبين
        </DialogTitle>
        <DialogDescription>الحلاقين المحجوبين لا يستطيعون إضافة ساكنين جدد</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">الحلاقين المحجوبين حالياً</label>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {blockedBarbers.length > 0 ? (
              blockedBarbers.map((b) => (
                <div
                  key={b.id}
                  className="flex items-center justify-between p-2 bg-red-500/10 border border-red-500/20 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <Ban className="w-4 h-4 text-red-400" />
                    <span>{b.name}</span>
                  </div>
                  <Button
                    onClick={() => onUnblock(b.id)}
                    variant="ghost"
                    size="sm"
                    className="text-green-500 hover:text-green-400"
                    title="إلغاء الحجب"
                  >
                    <ShieldOff className="w-4 h-4" />
                  </Button>
                </div>
              ))
            ) : (
              <p className="text-muted-foreground text-sm text-center py-2">لا يوجد حلاقين محجوبين</p>
            )}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">حجب حلاق</label>
          <Select onValueChange={onBlock}>
            <SelectTrigger className="bg-secondary/50 border-white/10">
              <SelectValue placeholder="اختر حلاق لحجبه" />
            </SelectTrigger>
            <SelectContent className="bg-card border-white/10">
              {allUsers
                .filter((u) => u.role === 'barber' && !blockedBarbers.some((b) => b.id === u.id))
                .map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export const AuthorizedBarbersDialog = ({
  open,
  onOpenChange,
  authorizedBarbers,
  allUsers,
  onRevoke,
  onAuthorize,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-card border-white/10">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <UserCheck className="w-5 h-5 text-green-400" />
          إدارة المصرح لهم بإضافة الساكنين
        </DialogTitle>
        <DialogDescription>
          فقط المستخدمون المصرح لهم يمكنهم إضافة ساكنين جدد
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">المصرح لهم حالياً</label>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {authorizedBarbers.length > 0 ? (
              authorizedBarbers.map((b) => (
                <div
                  key={b.id}
                  className="flex items-center justify-between p-2 bg-green-500/10 border border-green-500/20 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-green-400" />
                    <span>{b.name}</span>
                    <Badge variant="outline" className="text-xs">
                      {b.role === 'barber' ? 'حلاق' : b.role}
                    </Badge>
                  </div>
                  <Button
                    onClick={() => onRevoke(b.id)}
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-400"
                    title="إلغاء التصريح"
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                </div>
              ))
            ) : (
              <p className="text-muted-foreground text-sm text-center py-2">
                لا يوجد مستخدمين مصرح لهم - جميع الحلاقين يمكنهم الإضافة (مع موافقة المدير)
              </p>
            )}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">إضافة تصريح</label>
          <Select onValueChange={onAuthorize}>
            <SelectTrigger className="bg-secondary/50 border-white/10">
              <SelectValue placeholder="اختر مستخدم لتصريحه" />
            </SelectTrigger>
            <SelectContent className="bg-card border-white/10">
              {allUsers
                .filter(
                  (u) =>
                    (u.role === 'barber' || u.role === 'admin') &&
                    !authorizedBarbers.some((b) => b.id === u.id)
                )
                .map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name} ({u.role === 'barber' ? 'حلاق' : 'مدير'})
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-sm text-yellow-400">
          <p>
            <strong>ملاحظة:</strong> إذا أضفت مستخدماً واحداً على الأقل، سيتم تقييد الإضافة فقط للمصرح لهم.
          </p>
          <p>إذا كانت القائمة فارغة، جميع الحلاقين يمكنهم الإضافة (مع الحاجة لموافقة المدير).</p>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);
